import { Card, CardContent, CardHeader } from '@/app/common/components/Card'
import { ProgramTermsArray } from '@/queries/academic_terms/schema'
import { ProgramYearsProps } from '@/queries/program/schemas'
import { cn } from '@/lib/utils'
import { dateToYYYYMMDD } from '../../utils'

interface ProgramTimelineProps {
  programSettings: ProgramYearsProps
  academicTerms: ProgramTermsArray
}

const formatTimezone = (timezone: string | null | undefined) => {
  if (!timezone) return 'UTC'
  // Replace underscores with spaces and format nicely
  return timezone.replace(/_/g, ' ')
}

export default function ProgramTimeline({
  programSettings,
  academicTerms,
}: ProgramTimelineProps) {
  return (
    <Card className="h-fit sticky top-6">
      <CardHeader>
        <div className="text-lg font-semibold text-gray-800">
          Program Timeline
        </div>
        <p className="text-sm text-gray-500 font-normal">
          Visual overview of your program schedule
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Program Start */}
        <div className="relative">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-3 h-3 rounded-full bg-primary-500 ring-4 ring-primary-100" />
            <div className="text-sm font-semibold text-gray-900">
              {programSettings.name} - Program Start
            </div>
          </div>
          <div className="ml-6 pl-4 border-l-[1px] border-gray-200 pb-6">
            <div className="text-sm text-gray-600">
              {dateToYYYYMMDD(programSettings.program_start_date)}
            </div>
            <div className="inline-flex items-center gap-1.5 mt-1 px-2 py-0.5 bg-slate-100 text-slate-700 rounded text-xs font-medium">
              <svg
                className="w-3 h-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {formatTimezone(programSettings.global_timezone_code)}
            </div>
          </div>
        </div>

        {/* Academic Terms / Awarding Periods */}
        {academicTerms.map((period) => {
          const hasAddedDates =
            period.awarding_period_start_date && period.awarding_period_end_date

          const isActive = period.status_name === 'Active'
          const isCompleted = period.status_name === 'Completed'
          const isPlanned = period.status_name === 'Planned'

          return (
            <div key={period.id} className="relative">
              <div className="flex items-center gap-3 mb-2">
                <div
                  className={cn(
                    'w-3 h-3 rounded-full ring-4',
                    isActive && 'bg-emerald-500 ring-emerald-100',
                    isCompleted && 'bg-emerald-500 ring-emerald-100',
                    isPlanned && 'bg-gray-400 ring-gray-100',
                    !isActive &&
                      !isCompleted &&
                      !isPlanned &&
                      'bg-gray-300 ring-gray-100'
                  )}
                />
                <div className="text-sm font-medium text-gray-800">
                  {period.academic_term.name}
                </div>
              </div>
              <div className="ml-6 pl-4 border-l border-gray-200 pb-6">
                {hasAddedDates ? (
                  <div className="space-y-1">
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">Start:</span>{' '}
                      {dateToYYYYMMDD(
                        period.awarding_period_start_date ?? 'Not Set'
                      )}
                    </div>
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">End:</span>{' '}
                      {dateToYYYYMMDD(
                        period.awarding_period_end_date ?? 'Not Set'
                      )}
                    </div>
                    <div
                      className={cn(
                        'inline-flex items-center gap-1.5 mt-2 px-2 py-1 rounded text-sm font-medium',
                        isActive && 'bg-emerald-50 text-emerald-700',
                        isCompleted && 'bg-emerald-50 text-emerald-700',
                        isPlanned && 'bg-gray-50 text-gray-700'
                      )}
                    >
                      {isActive && (
                        <>
                          <svg
                            className="w-3 h-3"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Active Now
                        </>
                      )}
                      {isCompleted && (
                        <>
                          <svg
                            className="w-3 h-3"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Completed
                        </>
                      )}
                      {isPlanned && (
                        <>
                          <svg
                            className="w-3 h-3"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Planned
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="inline-flex items-center gap-1.5 px-2 py-1 bg-amber-50 text-amber-700 rounded text-sm font-medium">
                    <svg
                      className="w-3 h-3"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Dates Needed
                  </div>
                )}
              </div>
            </div>
          )
        })}

        {/* Program End */}
        <div className="relative">
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 rounded-full bg-primary-500 ring-4 ring-primary-100" />
            <div className="text-sm font-semibold text-gray-900">
              {programSettings.name} - Program End
            </div>
          </div>
          <div className="ml-6 pl-4 pt-2">
            <div className="text-sm text-gray-600">
              {dateToYYYYMMDD(programSettings.program_end_date)}
            </div>
            <div className="inline-flex items-center gap-1.5 mt-1 px-2 py-0.5 bg-slate-100 text-slate-700 rounded text-xs font-medium">
              <svg
                className="w-3 h-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {formatTimezone(programSettings.global_timezone_code)}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
